import { ChatSession, TurnTiming, TurnUsage } from '../../hooks/useChatSession';

interface MessageListProps {
  session: ChatSession;
  /** Budget offered by the "Retry with N" button on a truncated last reply. */
  retryTokens: number;
  onRetry: () => void;
  /** Adds time-to-first-token, total time and tokens/s under each reply. */
  showTiming?: boolean;
  /** Narrower bubbles for the two-column compare view. */
  compact?: boolean;
}

const MIN_RATE_WINDOW_S = 0.25;

const secs = (ms: number) => `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)}s`;

export function formatTiming(timing: TurnTiming, usage?: TurnUsage): string[] {
  const parts: string[] = [];
  if (timing.ttft !== null) parts.push(`${secs(timing.ttft)} to first token`);
  parts.push(`${secs(timing.total)} total`);
  // Output rate over the streaming window only, so queueing time doesn't skew it.
  // Skipped when the reply arrived in (nearly) one chunk: the rate would be noise.
  if (usage && usage.completion > 0 && timing.ttft !== null) {
    const window = (timing.total - timing.ttft) / 1000;
    if (window >= MIN_RATE_WINDOW_S) parts.push(`${Math.round(usage.completion / window)} tok/s`);
  }
  return parts;
}

/** The conversation for one chat session: bubbles, reasoning, usage, truncation retry. */
export default function MessageList({ session, retryTokens, onRetry, showTiming = false, compact = false }: MessageListProps) {
  const { messages, busy, reasoningByIndex, usageByIndex, finishByIndex, timingByIndex, errorByIndex } = session;
  const bubbleWidth = compact ? 'max-w-full' : 'max-w-2xl';

  return (
    <>
      {messages.map((m, i) => {
        const usage = usageByIndex[i];
        const timing = timingByIndex[i];
        const error = errorByIndex[i];
        return (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={m.role === 'assistant' ? `flex ${bubbleWidth} min-w-0 flex-col items-start` : 'min-w-0'}>
              <div
                className={`${bubbleWidth} whitespace-pre-wrap break-words rounded-[10px] border px-4 py-3 text-[13px] leading-relaxed ${
                  m.role === 'user'
                    ? 'border-ps-accent-line bg-[#2A160A] text-ps-text'
                    : 'border-ps-border bg-ps-chrome text-gray-200'
                }`}
              >
                {m.role === 'assistant' &&
                  reasoningByIndex[i] &&
                  (m.content ? (
                    // Uncontrolled so user toggles survive re-renders.
                    <details className="mb-2 border-b border-ps-border pb-2">
                      <summary className="cursor-pointer select-none text-xs font-medium text-ps-subtle">Reasoning</summary>
                      <div className="mt-1 whitespace-pre-wrap text-xs italic text-ps-subtle">{reasoningByIndex[i]}</div>
                    </details>
                  ) : (
                    <div className="mb-2 border-b border-ps-border pb-2">
                      <div className="text-xs font-medium text-ps-subtle">Reasoning</div>
                      <div className="mt-1 whitespace-pre-wrap text-xs italic text-ps-subtle">{reasoningByIndex[i]}</div>
                    </div>
                  ))}
                {m.content ||
                  (error ? (
                    <span className="text-ps-err">{error}</span>
                  ) : busy && i === messages.length - 1 ? (
                    !reasoningByIndex[i] && <span className="animate-pulse text-ps-subtle">…</span>
                  ) : (
                    !reasoningByIndex[i] && <span className="text-ps-subtle">Stopped before any output.</span>
                  ))}
              </div>
              {m.role === 'assistant' && usage && (
                <div className="mt-1.5 text-[11px] text-ps-subtle">
                  {usage.prompt.toLocaleString()} input · {usage.completion.toLocaleString()} output tokens · ₹
                  {usage.cost.toFixed(4)}
                </div>
              )}
              {m.role === 'assistant' && showTiming && timing && !error && (
                <div className="mt-0.5 text-[11px] text-ps-subtle">{formatTiming(timing, usage).join(' · ')}</div>
              )}
              {m.role === 'assistant' && finishByIndex[i] === 'length' && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-ps-warn">
                  <span>⚠ Truncated - hit the max-token limit</span>
                  {i === messages.length - 1 && !busy && (
                    <button
                      type="button"
                      onClick={onRetry}
                      className="rounded-md border border-ps-warn/40 bg-ps-warn-bg px-1.5 py-0.5 font-medium text-ps-warn transition-colors hover:border-ps-warn"
                    >
                      Retry with {retryTokens.toLocaleString()}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
